import React, { useState, useEffect } from "react";
import MainLayouts from "../../layout/MainLayouts";
import client from "../../api/client";
import { getCache, setCache } from "../../utils/cache";

const CACHE_KEY = "schedules_data";

export default function Schedule() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getCache(CACHE_KEY);
    if (cached) {
      setSchedules(cached);
      setLoading(false);
      return; // Serve from cache instantly
    }

    // Fetch schedules
    client.get("/v2/schedules")
      .then((response) => {
        const fetchedSchedules = response.data.schedules || [];
        setCache(CACHE_KEY, fetchedSchedules);
        setSchedules(fetchedSchedules);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching schedules:", err);
        setLoading(false);
      });
  }, []);

  // Process data for the grid
  const coursesMap = new Map();
  schedules.forEach(s => {
    if (s.course) {
      coursesMap.set(s.course.id, s.course.title);
    }
  });
  
  const courseIds = Array.from(coursesMap.keys()).sort((a, b) => coursesMap.get(a).localeCompare(coursesMap.get(b)));

  const weeksMap = new Map();
  let firstDate = null;
  if (schedules.length > 0) {
    firstDate = new Date(schedules[0].start_time);
    const day = firstDate.getDay();
    const diff = firstDate.getDate() - day + (day === 0 ? -6 : 1); // Align to Monday
    firstDate.setDate(diff);
    firstDate.setHours(0, 0, 0, 0);
  }

  schedules.forEach(s => {
    let weekNum = 1;
    if (firstDate) {
      const d = new Date(s.start_time);
      const diffTime = d - firstDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      weekNum = Math.floor(diffDays / 7) + 1;
    }
    
    if (!weeksMap.has(weekNum)) {
      weeksMap.set(weekNum, []);
    }
    weeksMap.get(weekNum).push(s);
  });

  // Ensure Week 6 exists even if empty, for the BREAK
  if (!weeksMap.has(6)) {
    weeksMap.set(6, []);
  }

  const weekNumbers = Array.from(weeksMap.keys()).sort((a, b) => a - b);

  let lastKnownMonth = 'Unknown';
  
  const weeksData = weekNumbers.map(weekNum => {
    const weekSchedules = weeksMap.get(weekNum);
    let earliestDate = null;
    const courseSessions = {};
    
    courseIds.forEach(cId => {
      courseSessions[cId] = [];
    });

    weekSchedules.forEach(s => {
      if (s.course && courseSessions[s.course.id]) {
        courseSessions[s.course.id].push(s);
      }
      const d = new Date(s.start_time);
      if (!earliestDate || d < earliestDate) {
        earliestDate = d;
      }
    });

    let maxSessions = 0;
    courseIds.forEach(cId => {
      courseSessions[cId].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      if (courseSessions[cId].length > maxSessions) {
        maxSessions = courseSessions[cId].length;
      }
    });

    if (maxSessions === 0) maxSessions = 1;

    if (earliestDate) {
      lastKnownMonth = earliestDate.toLocaleString('default', { month: 'long' });
    }
    const monthName = earliestDate ? lastKnownMonth : lastKnownMonth;

    return {
      weekNum,
      monthName,
      maxSessions,
      courseSessions
    };
  });

  const monthSpans = {};
  weeksData.forEach(wd => {
    if (!monthSpans[wd.monthName]) monthSpans[wd.monthName] = 0;
    monthSpans[wd.monthName] += wd.maxSessions;
  });

  const finalRows = [];
  let currentMonth = null;

  weeksData.forEach(wd => {
    for (let i = 0; i < wd.maxSessions; i++) {
      const isFirstInMonth = wd.monthName !== currentMonth;
      if (isFirstInMonth) {
        currentMonth = wd.monthName;
      }
      
      const isFirstInWeek = i === 0;
      
      const rowSessions = courseIds.map(cId => {
        return wd.courseSessions[cId][i] || null;
      });

      finalRows.push({
        isFirstInMonth,
        monthName: wd.monthName,
        monthSpan: monthSpans[wd.monthName],
        isFirstInWeek,
        weekNum: wd.weekNum,
        weekSpan: wd.maxSessions,
        rowSessions
      });
    }
  });

  return (
    <MainLayouts>
      <div className="bg-[#f4f6f9] min-h-screen overflow-x-hidden">
        {/* Header section */}
        <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-[#0b0f19] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0f19] to-[#1a2333]/30"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary rounded-full blur-[150px] opacity-10"></div>
          
          <div className="relative z-10 max-w-[1560px] mx-auto px-6 text-center">
            <h1 className="text-white text-4xl md:text-6xl font-bold mb-6">
              Cohort Class Schedule<span className="text-primary">.</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-16">
              Stay up-to-date with your live sessions, workshops, and cohort milestones.
            </p>
          </div>
        </div>

        {/* Schedule Content */}
        <div className="max-w-[1400px] mx-auto px-0 sm:px-6 py-16">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-primary rounded-full animate-spin"></div>
            </div>
          ) : courseIds.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0b0f19] mb-2">No upcoming schedules found</h3>
              <p className="text-gray-500">Check back later for updated cohort schedules.</p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full bg-white sm:rounded-xl shadow-sm" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full min-w-[800px] text-sm text-center border-collapse border border-gray-800">
                <thead>
                  <tr>
                    <th rowSpan={2} className="border border-gray-800 p-3 font-bold bg-gray-50 w-24">Month</th>
                    <th rowSpan={2} className="border border-gray-800 p-3 font-bold bg-gray-50 w-24">Week</th>
                    {courseIds.map(cId => (
                      <th key={cId} colSpan={2} className="border border-gray-800 p-3 font-bold bg-gray-50 text-gray-900">
                        {coursesMap.get(cId)}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {courseIds.map(cId => (
                      <React.Fragment key={`sub-${cId}`}>
                        <th className="border border-gray-800 p-2 font-semibold bg-gray-50">Date</th>
                        <th className="border border-gray-800 p-2 font-semibold bg-gray-50">Time</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {finalRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50/50 transition-colors">
                      {row.isFirstInMonth && (
                        <td rowSpan={row.monthSpan} className="border border-gray-800 p-3 font-bold align-middle bg-white">
                          {row.monthName}
                        </td>
                      )}
                      {row.isFirstInWeek && (
                        <td rowSpan={row.weekSpan} className="border border-gray-800 p-3 font-semibold align-middle bg-white whitespace-nowrap">
                          Week {row.weekNum}
                        </td>
                      )}
                      
                      {row.weekNum === 6 && row.rowSessions.every(s => !s) ? (
                        <td colSpan={courseIds.length * 2} className="border border-gray-800 p-4 font-bold uppercase tracking-[0.2em] bg-gray-100 text-center">
                          BREAK
                        </td>
                      ) : (
                        row.rowSessions.map((session, sIdx) => {
                          if (!session) {
                            return (
                              <React.Fragment key={`empty-${sIdx}`}>
                                <td className="border border-gray-800 p-2 text-gray-300"></td>
                                <td className="border border-gray-800 p-2 text-gray-300"></td>
                              </React.Fragment>
                            );
                          }
                          
                          if (session.topic && (session.topic.toUpperCase().includes('BREAK') || session.topic.toUpperCase().includes('HOLIDAY'))) {
                            return (
                              <td key={session.id} colSpan={2} className="border border-gray-800 p-2 font-bold text-center uppercase tracking-wider bg-gray-100 text-gray-800">
                                {session.topic}
                              </td>
                            );
                          }

                          const dateObj = new Date(session.start_time);
                          const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
                          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          
                          let endTimeStr = "";
                          if (session.duration_minutes) {
                            const end = new Date(dateObj.getTime() + session.duration_minutes * 60000);
                            endTimeStr = " - " + end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          }

                          return (
                            <React.Fragment key={session.id}>
                              <td className="border border-gray-800 p-3 font-medium whitespace-nowrap text-gray-800">
                                {dateStr}
                              </td>
                              <td className="border border-gray-800 p-3 whitespace-nowrap text-gray-700">
                                {timeStr}{endTimeStr}
                              </td>
                            </React.Fragment>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayouts>
  );
}
